import { Box, Flex, Text, useBreakpointValue } from '@chakra-ui/react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

type SubmissionOverTimeAreaChartData = {
  points: Array<{
    label: string;
    fullLabel: string;
    count: number;
  }>;
};

interface Props {
  data: SubmissionOverTimeAreaChartData;
  height?: number;
  showAllTicks?: boolean;
  axes?: {
    x?: string;
    y?: string;
  };
}

const MAX_MAIN_GRAPH_TICKS = 4;
const MAX_ZOOMED_GRAPH_TICKS = 10;

const getAxisTicks = (labels: string[], maxTicks: number) => {
  if (labels.length <= maxTicks) {
    return labels;
  }

  const ticks = new Set<string>();
  const lastIndex = labels.length - 1;
  const firstLabel = labels[0];
  const lastLabel = labels[lastIndex];

  if (firstLabel) {
    ticks.add(firstLabel);
  }

  if (lastLabel) {
    ticks.add(lastLabel);
  }

  for (let index = 1; index < maxTicks - 1; index += 1) {
    const ratio = index / (maxTicks - 1);
    const tickIndex = Math.round(ratio * lastIndex);
    const tickLabel = labels[tickIndex];

    if (tickLabel) {
      ticks.add(tickLabel);
    }
  }

  return labels.filter(label => ticks.has(label));
};

export const SubmissionOverTimeAreaChart = ({
  data,
  height = 300,
  showAllTicks = false,
  axes
}: Props) => {
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false;
  const labels = data.points.map(point => point.label);
  const axisTicks = getAxisTicks(
    labels,
    showAllTicks
      ? isMobile
        ? 6
        : MAX_ZOOMED_GRAPH_TICKS
      : isMobile
        ? 3
        : MAX_MAIN_GRAPH_TICKS
  );
  const chartHeight = isMobile
    ? Math.max(height - 44, 240)
    : axes?.x
      ? height - 24
      : height;
  const xAxisHeight = showAllTicks ? (isMobile ? 42 : 48) : isMobile ? 42 : 30;
  const showDot = data.points.length <= (showAllTicks ? 20 : isMobile ? 8 : 12);

  return (
    <Box>
      <Flex align="stretch">
        {axes?.y ? (
          <Flex align="center" justify="center" pr={2} minW="28px">
            <Text
              textStyle="textContext"
              textAlign="center"
              sx={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
            >
              {axes.y}
            </Text>
          </Flex>
        ) : null}
        <Box flex="1">
          <ResponsiveContainer width="100%" height={chartHeight}>
            <AreaChart
              data={data.points}
              margin={{
                top: 16,
                right: showAllTicks ? (isMobile ? 12 : 28) : isMobile ? 8 : 16,
                left: isMobile ? 0 : 4,
                bottom: isMobile ? 12 : 8
              }}
            >
              <defs>
                <linearGradient
                  id="submissionOverTimeArea"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor="#059669" stopOpacity={0.32} />
                  <stop offset="100%" stopColor="#059669" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="4 4"
                stroke="#D9E2EC"
                vertical={false}
              />
              <XAxis
                dataKey="label"
                axisLine={false}
                tickLine={false}
                interval={0}
                minTickGap={
                  showAllTicks ? (isMobile ? 8 : 12) : isMobile ? 16 : 24
                }
                ticks={axisTicks}
                padding={{
                  left: isMobile ? 0 : 8,
                  right: showAllTicks ? (isMobile ? 12 : 24) : isMobile ? 0 : 8
                }}
                angle={0}
                textAnchor="middle"
                height={xAxisHeight}
                tick={{ fill: '#334155', fontSize: isMobile ? 10 : 12 }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                width={isMobile ? 32 : 40}
                tick={{ fill: '#334155', fontSize: isMobile ? 10 : 12 }}
              />
              <Tooltip
                contentStyle={{
                  borderRadius: 12,
                  border: '1px solid #CBD5E1',
                  boxShadow: '0 12px 28px rgba(15, 23, 42, 0.14)',
                  backgroundColor: '#FFFFFF'
                }}
                labelFormatter={(_, payload) =>
                  payload?.[0]?.payload?.fullLabel ?? ''
                }
                formatter={value => [`${value}`, 'Submissions']}
              />
              <Area
                type="monotone"
                dataKey="count"
                stroke="#059669"
                strokeWidth={3}
                fill="url(#submissionOverTimeArea)"
                dot={
                  showDot
                    ? {
                        r: 4,
                        fill: '#059669'
                      }
                    : false
                }
                activeDot={{
                  r: 6,
                  fill: '#FFFFFF',
                  stroke: '#059669',
                  strokeWidth: 2
                }}
                isAnimationActive
                animationDuration={1100}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </Flex>
      {axes?.x ? (
        <Text mt={2} textAlign="center" textStyle="textContext">
          {axes.x}
        </Text>
      ) : null}
    </Box>
  );
};
